/**
 * usePracticeURL.ts — Extracted from PracticeView.vue Phase 5b
 *
 * Contains URL state save/restore functions: restoreStateFromPracticeSettings
 * and copyUrlToClipboard.
 * This is a purely mechanical extraction with zero logic changes.
 *
 * State remains in PracticeView's reactive object; this composable operates
 * on Ref-wrapped state properties passed via the deps interface.
 */
import type { AliyahType, BarMitzvahType, HaftarahType, SentenceGroupType, TagType } from 'types/types'
import { ComputedRef, nextTick, Ref } from 'vue'
import { VueLogger } from 'vue-logger-plugin'
import { RouteLocationNormalizedLoaded } from 'vue-router'

import { NO_FILTER_SENTINEL } from '@/constants/appConstants'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import type { PracticeSettings } from '@/util/PracticeSettings'
import { TransformationType } from '@/util/Transformations'
import { VoiceStyles, VoiceStyleSet } from '@/util/VoiceStyles'

// {{{ interface
export interface PracticeURLDeps {
  // State refs (from PracticeView's toRefs)
  aliyah: Ref<AliyahType | null>
  annotatedSentences: Ref<Array<AnnotatedSentence>>
  choosableHiddenTagsListeners: Ref<Set<() => void>>
  focusSentenceIndex: Ref<number | null>
  haftarah: Ref<HaftarahType | null>
  readOrder: Ref<number>
  selectedHiddenTag: Ref<TagType | null>
  selectedNonHiddenTags: Ref<Array<TagType>>
  sentenceGroup: Ref<SentenceGroupType | null>
  surroundLines: Ref<number>
  tikkunStyle: Ref<boolean>
  transformation: Ref<TransformationType | null>
  voiceStyleSet: Ref<VoiceStyleSet>

  // Computed refs
  choosableHiddenTags: ComputedRef<Array<TagType>>
  choosableNonHiddenTags: ComputedRef<Array<TagType>>
  relevantTransformations: ComputedRef<Array<TransformationType>>

  // Callbacks
  handleSentenceClick: (annotatedSentence: AnnotatedSentence, sentenceIndex: number | null) => void

  // External
  route: RouteLocationNormalizedLoaded
  store: {
    getters: {
      student: {
        barMitzvah: BarMitzvahType | null
        practiceSettings: PracticeSettings | null
      }
    }
    dispatch: {
      snackbar: { add: (payload: { message: string; state: string }) => void }
    }
  }
}
// }}}

export function usePracticeURL(logger: VueLogger, deps: PracticeURLDeps) {
  // {{{ restoreStateFromPracticeSettings
  /**
   * CHAIN 4: URL state restoration
   * @precondition Called from onMounted after fetchBarMitzvah resolves; store.getters.student.practiceSettings is set
   * @postcondition UI state matches URL parameters exactly after all nextTick chains complete
   * @invariant Nested nextTick chains ensure computed properties settle before dependent code runs.
   *   Tag restoration uses the choosableHiddenTagsListeners deferred listener pattern (Chain 1).
   * @sideeffect Sets aliyah, haftarah, sentenceGroup, voiceStyleSet.voice (triggers Chain 2, 3 watchers)
   * @sideeffect Registers listener on choosableHiddenTagsListeners for deferred tag selection
   * @async-note Uses 4 levels of nested nextTick: (1) register listener, (2) set tags + sentence focus,
   *   (3) set transformation/tikkun/readOrder/surroundLines, (4) set nonHiddenTags.
   *   Each level waits for the previous computed properties to settle.
   */
  const restoreStateFromPracticeSettings = () => {
    // prevent the watchEffect from interfering while we set values; this gets
    // unset as part of the very last callback
    const ps: PracticeSettings | null = deps.store.getters.student.practiceSettings

    if (!ps) {
      return
    }

    if (ps.a) {
      deps.aliyah.value =
        deps.store.getters.student.barMitzvah?.aliyot.find((aliyah: AliyahType) => {
          return aliyah.id === ps.a
        }) ?? null
    }

    // note that we ignore the value of h itself; we just take this as the URL wanting to select the bar mitzvah's haftarah
    if (ps.h) {
      if (deps.store.getters.student.barMitzvah?.haftarah) {
        deps.haftarah.value = deps.store.getters.student.barMitzvah?.haftarah
      }
    }

    if (ps.sg) {
      deps.sentenceGroup.value =
        deps.store.getters.student.barMitzvah?.sentenceGroups.find((sentenceGroup: SentenceGroupType) => {
          return sentenceGroup.reading.id === ps.sg
        }) ?? null
    }

    if (ps.v && ps.v !== NO_FILTER_SENTINEL) {
      deps.voiceStyleSet.value.voice = VoiceStyles[parseInt(ps.v)]
    }

    // prepare the callback for when computed choosableHiddenTags is done.
    const setTagsFromURL = () => {
      // selectedHiddenTag
      if (ps.ht) {
        logger.debug(`onMounted choosableHiddenTags.value =`)
        logger.debug(deps.choosableHiddenTags.value)
        deps.selectedHiddenTag.value =
          deps.choosableHiddenTags.value.find((choosableHiddenTag: TagType) => {
            return choosableHiddenTag.name === ps.ht
          }) ?? null
        logger.debug(`onMounted state.selectedHiddenTag =`)
        logger.debug(deps.selectedHiddenTag.value)
      }

      nextTick(() => {
        nextTick(() => {
          // focusSentenceIndex
          if (ps.fs) {
            const fsi = parseInt(ps.fs)
            deps.handleSentenceClick(deps.annotatedSentences.value[fsi] as AnnotatedSentence, fsi)
          }

          // transformation
          if (ps.t) {
            deps.transformation.value = deps.relevantTransformations.value[parseInt(ps.t)]
          }

          // tikkunStyle
          if (ps.tk) {
            deps.tikkunStyle.value = ps.tk === 'true'
          }

          // state.readOrder
          if (ps.ro) {
            deps.readOrder.value = parseInt(ps.ro)
          }

          // state.surroundLines
          if (ps.sl) {
            deps.surroundLines.value = parseInt(ps.sl)
          }

          nextTick(() => {
            nextTick(() => {
              if (ps.nht) {
                ps.nht.split(',').forEach((tagId: string) => {
                  const foundTag =
                    deps.choosableNonHiddenTags.value.find((choosableNonHiddenTag: TagType) => {
                      return choosableNonHiddenTag.id === tagId
                    }) ?? null
                  if (foundTag) {
                    deps.selectedNonHiddenTags.value.push(foundTag)
                  }
                })
              }
            })
          })
        })
      })
    }

    nextTick(() => {
      const listener = () => {
        setTagsFromURL()
        deps.choosableHiddenTagsListeners.value.delete(listener)
      }
      deps.choosableHiddenTagsListeners.value.add(listener)
    })
  }
  // }}}

  // {{{ copyUrlToClipboard
  /**
   * @postcondition Practice URL with all current settings copied to clipboard; snackbar shown
   * @sideeffect Writes to navigator.clipboard; dispatches snackbar.add
   * @depends-on deps.aliyah, haftarah, sentenceGroup, selectedHiddenTag, selectedNonHiddenTags,
   *   focusSentenceIndex, tikkunStyle, readOrder, surroundLines, transformation, voiceStyleSet
   */
  const copyUrlToClipboard = (): void => {
    const a = deps.aliyah.value?.id ?? ''
    const h = deps.haftarah.value?.reading.id ?? ''
    const sg = deps.sentenceGroup.value?.reading.id ?? ''
    const ht = deps.selectedHiddenTag.value?.name ?? ''
    const nht = deps.selectedNonHiddenTags.value ? deps.selectedNonHiddenTags.value.map((tag: TagType) => tag.id).join(',') : ''
    const fs = deps.focusSentenceIndex.value ?? ''
    const tk = deps.tikkunStyle.value
    const ro = deps.readOrder.value
    const sl = deps.surroundLines.value
    const t = deps.relevantTransformations.value.findIndex((transformation: TransformationType) => {
      return transformation.name === deps.transformation.value?.name
    })
    const v = VoiceStyles.indexOf(deps.voiceStyleSet.value.voice)

    const url = `${window.location.origin}${deps.route.path}?a=${a}&h=${h}&sg=${sg}&v=${v}&ht=${ht}&fs=${fs}&t=${t}&tk=${tk}&ro=${ro}&sl=${sl}&nht=${nht}`
    navigator.clipboard
      .writeText(url)
      .then(() => {
        deps.store.dispatch.snackbar.add({ message: 'Practice URL copied to clipboard', state: 'primary' })
      })
      .catch(() => {})
  }
  // }}}

  return {
    copyUrlToClipboard,
    restoreStateFromPracticeSettings,
  }
}
